'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useMemo, useState } from 'react';
import { InputV2, TreeAccordion } from 'ui-components';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { getPublisherController } from '../facetControllers';
import { StringFlatFacetValue, useFlatFacet } from '../hooks/useFlatFacet';
import { PublisherFacetProps } from './PublisherFacet.types';

const getValueId = (v: StringFlatFacetValue): string => v.value;

export const PublisherFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: PublisherFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const publisherLabel =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.filter-publisher-label'
    )?.value ?? 'Publisher';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.clear')?.value ??
    'Clear';

  const searchPlaceholder =
    searchLabelsMicrocopy?.find(
      (m) => m.key === 'search.filter-publisher-placeholder'
    )?.value ?? 'Search publishers...';

  const [searchQuery, setSearchQuery] = useState('');

  const {
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  } = useFlatFacet({
    getController: getPublisherController,
    fieldName: 'publisher_name',
    getValueId,
  });

  const filteredValues = useMemo(() => {
    if (!searchQuery.trim()) return optimisticValues;
    const query = searchQuery.toLowerCase();
    return optimisticValues.filter((v) =>
      v.value.toLowerCase().includes(query)
    );
  }, [optimisticValues, searchQuery]);

  const isDark = variant === 'mobile';

  const treeItems = useMemo(
    () =>
      filteredValues.map((v) => ({
        id: getValueId(v),
        label: v.value,
        count: v.numberOfResults,
        disabled: v.numberOfResults === 0 && v.state !== 'selected',
      })),
    [filteredValues]
  );

  const content = (
    <div className="flex flex-col">
      <InputV2
        type="text"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder={searchPlaceholder}
        className="mb-1 w-full text-sm"
        aria-label={searchPlaceholder}
      />
      <div className="max-h-[200px] overflow-y-auto">
        <TreeAccordion
          items={treeItems}
          selectionMode="multiple"
          selectedIds={selectedIds}
          onSelectMany={handleSelectMany}
          isDarkTheme={isDark}
          size={variant === 'sidebar' ? 'default' : 'sm'}
        />
      </div>
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={publisherLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={publisherLabel}
      variant={variant}
      className={className}
      showClear={variant === 'drawer' && selectedCount > 0}
      onClear={handleClear}
      clearLabel={clearLabel}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
